import { Box, styled, Typography } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AdListingModel from "@/models/AdListingModel";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import { AdActions } from "@/app/shared/ad/AdActions";
import ListingCardRibbons from "./ListingCardRibbons";
import Link from "next/link";
import { AD_IMAGE_RATIO } from "@/services/AdImageConfig";
import Image from "next/image";

const Card = styled(Box)();

const Header = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    position: "relative",
    width: "100%",
    aspectRatio: 1 / AD_IMAGE_RATIO,
    borderRadius: 2,
    overflow: "hidden",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    "> *, img": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
  }),
);

const Body = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    position: "relative",
    paddingTop: 1,
    marginTop: -2,
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    overflow: "hidden",
  }),
);

// AJB: use RippleCard
export default function ListingCard({
  ad,
  isCompact,
  isSearchResult,
}: {
  ad: AdListingModel;
  isCompact?: boolean;
  isSearchResult?: boolean;
}) {
  const cardOpacity = ad.isInactive ? 0.5 : 1;

  return (
    <Card
      sx={{
        ...(isSearchResult
          ? {
              boxShadow: ad.isHighlighted ? 5 : 0,
              padding: 0,
              borderRadius: 4,
            }
          : {}),
      }}
    >
      <Header
        sx={{
          opacity: cardOpacity,
        }}
      >
        {!ad.primary_image || ad.gallery.items.length === 0 ? (
          <Box
            component={Link}
            href={ad.url}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              color: "grey.400",
            }}
          >
            <NoPhotographyIcon sx={{ fontSize: "100px" }} />
            <Typography variant="body2">No photography available</Typography>
          </Box>
        ) : (
          <Link href={ad.url}>
            <Image
              src={ad.primary_image}
              alt={ad.title}
              fill
              unoptimized
              loading="lazy"
              fetchPriority="high"
            />
          </Link>
        )}
      </Header>
      <ListingCardRibbons ad={ad} />
      <Body
        sx={(theme) => ({
          opacity: cardOpacity,
          height: isSearchResult ? "180px" : isCompact ? "120px" : "190px",
          padding:
            ad.isHighlighted && isSearchResult
              ? theme.spacing(1, 2)
              : theme.spacing(1, 0, isSearchResult ? 1 : 0, 0),
        })}
      >
        <Link href={ad.url}>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              flexShrink: 0,
              fontWeight: "bold",
              overflow: "hidden",
              ...(isCompact
                ? {
                    fontSize: "18px",
                  }
                : {
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }),
            }}
          >
            {ad.title}
          </Typography>
        </Link>
        {ad.detailsLines.map((line, lineIndex) => (
          <Box
            key={lineIndex}
            sx={{
              display: "flex",
              overflow: "hidden",
              flexShrink: 0,
              flexWrap: lineIndex === 0 ? "initial" : "wrap",
            }}
          >
            {line.map((value, valueIndex) => (
              <Box key={valueIndex}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {value}
                  {valueIndex < line.length - 1 && <>&nbsp;·&nbsp;</>}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
        <Typography
          variant="body2"
          sx={{
            color: "grey.600",
            overflow: "hidden",
            maxHeight: "80px",
          }}
        >
          {ad.descriptionShort}
        </Typography>
        <Box
          sx={{
            marginTop: "auto",
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            minWidth: 0,
            whiteSpace: "nowrap",
          }}
        >
          {ad.location && (
            <>
              {ad.distanceKms !== null ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    display: "inline",
                  }}
                >
                  ~ {ad.distanceKms} kms
                </Typography>
              ) : (
                ""
              )}
              <LocationOnIcon sx={(theme) => ({ ...theme.typography.body1 })} />
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                variant="body2"
              >
                {ad.location}
              </Typography>
            </>
          )}

          {!isCompact && (
            <Box
              sx={{
                marginLeft: "auto",
                display: "flex",
              }}
            >
              <AdActions ad={ad} iconOnly />
            </Box>
          )}
        </Box>
      </Body>
    </Card>
  );
}
